import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import EmailTemplateWithButtons from '../../Components/EmailTemplateWithButtons';
import { DropDown } from "../../Components/DropDown";
import { DefaultCard } from "../../Components/Card/Card";
import { bindActionCreators } from "redux";
import commonActionsImp from "../../Actions/commonActions";
import customerActionsImp from "../../Actions/customerActions";

export default function AdminFormsContainer({ }) {
    const dispatch = useDispatch();

    const { t } = useTranslation();
    const vonAccounts = useSelector(state => state.CustomerReducer.vonAccounts);
    const customer = useSelector(state => state.CustomerReducer.customer);
    const adminFormsList = useSelector(state => state.CommonReducer.dropdownlists?.AdminForms);
    const selectedAdminForm = useSelector(state => state.CustomerReducer.selectedAdminForm);
    const adminForm = useSelector(state => state.CommonReducer.forms?.adminForm);
    const commonActions = bindActionCreators(commonActionsImp, dispatch);
    const customerActions = bindActionCreators(customerActionsImp, dispatch);

    const [selectedOption, setSelectedOption] = useState('');

    const handleFormSelection = useCallback(event => {
        const formId = parseInt(event.target.value, 10);
        customerActions.selectAdminForm(formId);
        customerActions.getAdminFormTemplate(customer.vbCustomer?.customerNumber, customer.vonCustomer?.id ?? customer.vossCustomer?.id, formId); 
    }, [customer]);

    const handleFieldChange = useCallback((field, value) => {
        commonActions.changeMultiFormField("adminForm", field, value);
    }, []);

    const handleEmailTemplateChange = useCallback((field, value) => {
        commonActions.changeMultiFormField(adminForm?.id, field, value);
    }, [adminForm]);

    const handleSaveClick = useCallback(() => {
        customerActions.sendAdminForm(adminForm);
    }, [adminForm]);

    const handleCancelClick = useCallback(() => {
        commonActions.cancelFormEditing(adminForm?.id);
        customerActions.selectAdminForm(null);
    }, [adminForm]);

    const getSenderEmails = useCallback(() => {
        return adminForm?.data?.defaultSenderAddresses;
    }, [adminForm]);

    const handleDropDownSelect = useCallback(event => {
        handleFieldChange('to', event);
        setSelectedOption(event);
    }, [handleFieldChange]);

    const handleVbClick = useCallback(() => {
        handleFieldChange('to', customer.email);
        setSelectedOption(customer.email);
    }, [handleFieldChange, customer]);

    const handleVonClick = useCallback(() => {
        if (vonAccounts) {
            const primaryContact = vonAccounts.find(vonAccount => vonAccount.isPrimaryContact === true);
            if (primaryContact) {
                handleFieldChange('to', primaryContact.email);
                setSelectedOption(primaryContact.email);
            }
        }
    }, [handleFieldChange, vonAccounts]);

    useEffect(() => {
        commonActions.loadDropdownList('AdminForms');

        if ((customer.vonCustomer || customer.vossCustomer) && !vonAccounts) {
            customerActions.getVonAccounts(customer.vonCustomer.id ?? customer.vossCustomer.id);
        }
    }, []);

    useEffect(() => {
        return () => {
            customerActions.selectAdminForm(null);
        };
    }, []);

    const contactList = [];

    if (vonAccounts) {
        vonAccounts.forEach(vonAccount => {
            if (vonAccount.roles.find(role => role.role === "customeradmin")) {
                contactList.push({ name: vonAccount.name, id: vonAccount.email });
            }
        });
    }

    if (!adminFormsList) return null;

    return (
        <DefaultCard title={t('adminformscontainer.header')}>
            <div className="row mb-10">
                <div className="col-8">
                    <div className="input-group">
                        <select
                            className="form-control"
                            value={selectedAdminForm ?? -1}
                            onChange={handleFormSelection}
                        >
                            <option key={-1} disabled hidden value={-1}>
                                {t('adminformscontainer.select')}
                            </option>
                            {[...adminFormsList]
                                .sort((a, b) => a.name.localeCompare(b.name, 'sv'))
                                .map(item => (
                                    <option key={item.id} value={item.id}>
                                        {item.name}
                                    </option>
                                ))}
                        </select>
                    </div>
                </div>
            </div>
            {selectedAdminForm && (
                <div className="row">
                    {customer.vonCustomer && (
                        <div className="col-8">
                            <div>
                                <div className="form-group">
                                    <label className="col-xs-4 control-label"></label>
                                    <div className="col-xs-8 d-flex gap-8 align-items-center flex-wrap">
                                        <button
                                            className="btn btn-primary btn-sm"
                                            type="button"
                                            onClick={handleVonClick}
                                        >
                                            {t('emailtemplate.voncontact')}
                                        </button>
                                        <button
                                            className="btn btn-primary btn-sm"
                                            type="button"
                                            onClick={handleVbClick}
                                        >
                                            {t('emailtemplate.vbcontact')}
                                        </button>
                                    </div>
                                </div>
                                <DropDown
                                    label=" "
                                    blankOption={true}
                                    value={selectedOption}
                                    onChange={handleDropDownSelect}
                                    list={contactList}
                                />
                                <br />
                            </div>
                        </div>
                    )}
                    <div className="col-8">
                        <EmailTemplateWithButtons
                            data={adminForm && adminForm.data}
                            onChange={handleEmailTemplateChange}
                            onSaveClick={handleSaveClick}
                            onCancelClick={handleCancelClick}
                            senderEmails={getSenderEmails()}
                            onSenderChange={handleFieldChange}
                        />
                    </div>
                </div>
            )}
        </DefaultCard>
    );
}
